/* eslint-disable react-native-enforce-testid/testid-missing */
import { useNavigation } from "@react-navigation/native";
import React, { useEffect, useMemo, useState, memo } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Dimensions,
  PixelRatio,
  SectionList,
  ActivityIndicator,
  TouchableNativeFeedback,
  Image,
  BackHandler,
} from "react-native";
import { useSelector } from "react-redux";
import Tooltip from "react-native-walkthrough-tooltip";
import dayjs from "dayjs";

import debounce from "lodash/debounce";

import t from "@constants/localization";
import Text from "@components/AppText";
import colors from "@constants/color";
import { scale } from "@utils/scale";
import analytics from "@services/analytics";
import {
  MY_DELIVERY_EVENTS,
  SEARCH_EVENTS,
} from "@constants/analyticsEventsCT";
import CallIcon from "@assets/svg/newCall";
import Tick from "@assets/svg/MyDeliveries/Tick";
import Help from "@assets/svg/MyDeliveries/Help";
import { getDailyRTOSV3, getCrateDeliveryDetails } from "@services/api";
import { Cash, OrderReturnStatus } from "@assets/svg/MyDeliveries";
import { getDayMonth } from "@utils/getFormattedDate";
import Info from "@assets/svg/MyDeliveries/Info";
import { NewCustomerPriorityInfoWidget } from "@components/NewCustomerPriorityInfoWidget";
import NeededButton from "@components/DeliveryRTOOrder/NeededButton";
import CustomText from "@components/CustomText";
import RTOModal from "@components/DeliveryRTOOrder/RTOModal";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import SearchIcon from "@assets/svg/SearchIcon";
import { searchAndFilterData } from "@components/SearchHeader/utils";
import EmptySearchList from "@components/SearchHeader/EmptySearchList";
import Tag from "@components/Tag";
import MetaStrip from "../../components/MetaStrip";

import DeliveryOrderCount from "./components/DeliveryOrderCount";

const PATH = "screens/MyDeliveries/DeliveryOrdersList";
const { width } = Dimensions.get("window");
const x = width / 360;

export const NewTag = () => {
  return (
    <View style={styles.newTag}>
      <Text style={styles.newTagText}>{t(`${PATH}/new`)}</Text>
    </View>
  );
};

const onPressCall = (phone, callEventProps = {}) => {
  handleCallButtonPress(
    phone,
    MY_DELIVERY_EVENTS.CUSTOMER_CALLED,
    callEventProps,
  );
};

function normalize(size) {
  const newSize = size * x;
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
}

const DeliveryOrdersList = ({ route }) => {
  const navigation = useNavigation();
  const [ordersData, setOrdersData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [rtoFeedbackModal, setRTOFeedbackModal] = useState(false);
  const {
    forNotDelivered = false,
    date = "",
    order_ids = "",
    spokeName = "",
    type = "",
    reasons = "",
  } = route?.params || {};
  const checkFromFPN = !!(date && order_ids && spokeName && type && reasons);
  const [action, setAction] = useState({
    type: checkFromFPN ? type : "",
    reasons: checkFromFPN ? reasons : "",
    order_ids: checkFromFPN ? order_ids : "",
  });
  const deliveryCommunicationEnabled = useSelector(
    (state) => state?.configReducer?.delivery_communication_enabled,
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const backHandler = () => {
      if (isSearchEnabled) {
        onSearchInputChange("");
        setIsSearchEnabled((prev) => !prev);
        return true;
      }
      return false;
    };

    const subscription = BackHandler.addEventListener("hardwareBackPress", backHandler);

    return () => {
      subscription.remove();
    };
  }, [isSearchEnabled, onSearchInputChange]);

  const fetchData = async () => {
    try {
      if (forNotDelivered) {
        const { data } = await getDailyRTOSV3({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
        });
        setOrdersData(data);
      } else {
        const { data } = await getCrateDeliveryDetails({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
          type: "NOT_ASSIGNED",
        });
        setOrdersData(data);
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const onCardPress = (item) => {
    if (item?.orders_list?.length > 1) {
      navigation.navigate("UsersOrderList", {
        user: { user_id: item?.user_id },
        date: route?.params?.date,
        headerTitle: `${item?.user_name}`,
        subHeaderTitle: t(`${PATH}/subHeaderTitle`, {
          date: getDayMonth(route?.params?.date),
        }),
        eventProps: {
          event: "Order Tile Clicked",
          screen: "Delivery Details",
          tab: route?.params?.tab || "",
          section: "Deliveries",
        },
      });
    } else {
      navigation.navigate("OrderDetails", {
        order_id: item?.orders_list[0],
      });
    }
    analytics.trackEvent(MY_DELIVERY_EVENTS.CUSTOMER_TILE_CLICKED, {
      user_id: item.user_id,
      screen: "Delivery Details",
      tab: route?.params?.tab || "",
      section: "Deliveries",
    });
  };

  const sortUpdates = useMemo(
    () =>
      function () {
        const sortedUpdateObjects = {};
        const ordersDataArray = Object.keys(ordersData);
        ordersDataArray.forEach((key) => {
          if (Array.isArray(ordersData[key]) && ordersData[key].length !== 0) {
            sortedUpdateObjects[key] = ordersData[key];
          }
        });
        return Object.keys(sortedUpdateObjects).map((key) => {
          if (key === "usersToBeDelivered") {
            return {
              data: sortedUpdateObjects[key],
            };
          } else {
            return {
              key,
              data: sortedUpdateObjects[key],
            };
          }
        });
      },
    [ordersData],
  );

  const getSectionBackgroundColor = (sectionKey = "") => {
    switch (sectionKey) {
      case "usersToBeDeliveredFirstTime":
        return colors.plain_green;
      case "usersUnableToDeliver":
        return "#FFEAEA";
      default:
        return "transparent";
    }
  };

  const closeRTOModal = () => {
    fetchData();
    setRTOFeedbackModal(false);
    setAction({ type: "", reasons: "", order_ids: "" });
  };

  // eslint-disable-next-line no-shadow
  const onPressNeeded = (type = "", reasons = "", item = {}) => {
    setAction({ type, reasons, order_ids: item?.orders_list.join(",") });
    setRTOFeedbackModal(true);
  };

  const renderItem = ({ item, section }) => (
    <Card item={item} section={section} />
  );

  const renderSection = ({ section: { key, data } }) => {
    if (key && key === "usersToBeDeliveredFirstTime") {
      return (
        <>
          {key === "usersToBeDeliveredFirstTime" &&
          Array.isArray(ordersData?.metadata?.newCxPriorityInfo) ? (
            <NewCustomerPriorityInfoWidget
              newCxPriorityInfo={ordersData.metadata.newCxPriorityInfo}
              showBottomLine={false}
            />
          ) : null}
          <View
            style={{
              padding: 8,
              backgroundColor: getSectionBackgroundColor(key),
            }}
          />
        </>
      );
    } else if (key) {
      return (
        <View
          style={[
            styles.sectionHeader,
            {
              backgroundColor: getSectionBackgroundColor(key),
            },
          ]}
        >
          <OrderReturnStatus
            color={
              key === "usersToBeDeliveredFirstTime"
                ? getSectionBackgroundColor(key)
                : ""
            }
            orderStatus={key}
          />
          <Text
            style={[
              styles.sectionHeaderText,
              {
                color:
                  key === "usersUnableToDeliver"
                    ? colors.plain_red
                    : colors.plain_green,
              },
            ]}
          >
            {t(
              `${PATH}/${key}/${
                ordersData?.ordersCount[key] === 1 ? "singular" : "plural"
              }`,
              {
                count: ordersData?.ordersCount[key],
              },
            )}
          </Text>
        </View>
      );
    } else {
      return (
        <View
          style={{
            padding: 8,
          }}
        />
      );
    }
  };

  const keyExtractor = (item, index) => `${item.user_id}-${index}`;

  const renderExtraInfo = (infoData, index) => {
    // eslint-disable-next-line no-shadow
    const { uri = "", text = "", type = "" } = infoData || {};
    return (
      <View key={`${type}${index}`} style={styles.orderNeededInstructions}>
        <Image
          resizeMode="contain"
          style={styles.footerImage}
          source={{ uri }}
        />
        <CustomText
          variant="body_text_2"
          newfont={true}
          style={[
            styles.orderNeeded,
            { color: infoData?.color ? infoData?.color : colors.mediumBlack },
          ]}
        >
          {text}
        </CustomText>
      </View>
    );
  };

  const Card = ({ item, section }) => {
    const callEventProps = {
      user_id: item?.user_id,
      date: route?.params?.date,
      header_tab: route?.params?.tab || "",
      order_ids: item?.orders_list.join(","),
    };
    return (
      <View
        style={[
          {
            backgroundColor: getSectionBackgroundColor(section.key),
          },
          styles.cardContainer,
        ]}
      >
        <TouchableNativeFeedback
          background={TouchableNativeFeedback.Ripple("rgba(0, 0, 0, 0.05)")}
          onPress={() => {
            onCardPress(item);
          }}
        >
          <View style={styles.listView}>
            {item?.new_user && <NewTag />}
            <View style={styles.view1}>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text style={styles.name}>{item?.user_name}</Text>
                {item?.is_priority_cx && (
                  <View style={{ marginLeft: 6 }}>
                    <Tag text="Priority" />
                  </View>
                )}
              </View>

              <TouchableOpacity
                onPress={() => onPressCall(item?.user_phone, callEventProps)}
              >
                <CallIcon
                  strokeColor="#2195F2"
                  width={normalize(17)}
                  height={normalize(17)}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.address}>{item?.cx_formatted_address}</Text>
            {item?.COD && (
              <View style={styles.view2}>
                <View>
                  <Text style={styles.text2}>{t(`${PATH}/${"items"}`)}</Text>
                  <DeliveryOrderCount data={item?.COD} />
                </View>
                <View style={styles.separator} />
                <View>
                  <View style={{ flexDirection: "row" }}>
                    <Text style={styles.text2}>{t(`${PATH}/amount`)}</Text>
                    <PaymentMode online={false} />
                  </View>
                  <Text style={styles.text3}>
                    {t(`${PATH}/rs`)}
                    {item?.COD?.amount}
                  </Text>
                </View>
              </View>
            )}
            {item?.PREPAID && (
              <View style={styles.view2}>
                <View>
                  <Text style={styles.text2}>{t(`${PATH}/${"items"}`)}</Text>
                  <DeliveryOrderCount data={item?.PREPAID} />
                </View>
                <View style={styles.separator} />
                <View>
                  <View style={{ flexDirection: "row" }}>
                    <Text style={styles.text2}>{t(`${PATH}/amount`)}</Text>
                    <PaymentMode online={true} />
                  </View>
                  <Text style={styles.text3}>
                    {t(`${PATH}/rs`)}
                    {item?.PREPAID?.amount}
                  </Text>
                </View>
              </View>
            )}
            {item?.extra_commission_strip ? (
              <MetaStrip
                text={item.extra_commission_strip}
                smallStrip
                extraStyling={{ marginTop: 8 }}
              />
            ) : null}

            {deliveryCommunicationEnabled && item?.info
              ? item?.info.map(renderExtraInfo)
              : null}
            {deliveryCommunicationEnabled && item?.submit_buttons ? (
              <NeededButton
                // eslint-disable-next-line no-shadow
                onPressNeeded={(type, reasons) =>
                  onPressNeeded(type, reasons, item)
                }
                checkNeededLoading={false}
                checkNotNeededLoading={false}
                containerStyle={styles.neededButtonContainer}
                submitButtons={item.submit_buttons}
              />
            ) : null}
          </View>
        </TouchableNativeFeedback>
      </View>
    );
  };

  const onSearchInputChange = debounce(async (text) => {
    setIsLoading(true);
    try {
      if (forNotDelivered) {
        const { data } = await getDailyRTOSV3({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
        });
        const filteredData = searchAndFilterData(text, data);
        setOrdersData(filteredData);
      } else {
        const { data } = await getCrateDeliveryDetails({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
          type: "NOT_ASSIGNED",
        });
        const filteredData = searchAndFilterData(text, data);
        setOrdersData(filteredData);
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  }, 500);

  const onPressCrossIcon = () => {
    fetchData();
    analytics.trackEvent(SEARCH_EVENTS.SEARCH_CROSS_CLICKED, {
      screen: "Delivery Details",
    });
  };
  const onPressSearchIcon = () => {
    setIsSearchEnabled(!isSearchEnabled);
    analytics.trackEvent(SEARCH_EVENTS.SEARCH_CLICKED, {
      screen: "Delivery Details",
    });
  };
  const goingBackHandler = () => {
    fetchData();
    setIsSearchEnabled(false);
  };

  const checkOrderDatailsEmpty = () => {
    if (ordersData) {
      if (
        ordersData?.usersDelivered?.length > 0 ||
        ordersData?.usersToBeDelivered?.length > 0 ||
        ordersData?.usersToBeDeliveredFirstTime?.length > 0 ||
        ordersData?.usersUnableToDeliver?.length > 0
      ) {
        return false;
      }
    }
    return true;
  };

  return (
    <View style={{ flex: 1 }}>
      <HeaderWithSearch
        headertitle={
          !isSearchEnabled
            ? forNotDelivered
              ? t(`${PATH}/returnedOrders`)
              : t(`${PATH}/deliveryDetails`, {
                  date: getDayMonth(route?.params?.date),
                })
            : t(`SearchOrder/title`)
        }
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        isSearchEnabled={isSearchEnabled}
        searchPlaceholder={t(`SearchOrder/placeholder`)}
        onPressCrossIcon={onPressCrossIcon}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        onSearchInputChange={onSearchInputChange}
        searchIcon={<SearchIcon height={24} width={24} />}
        onPressSearchIcon={onPressSearchIcon}
        rightComponent={
          !forNotDelivered ? (
            <TouchableOpacity
              onPress={() => {
                navigation.navigate("SupportTree");
              }}
              style={styles.helpBtn}
            >
              <Help />
              <Text style={styles.helpBtnText}>{t(`${PATH}/help`)}</Text>
            </TouchableOpacity>
          ) : null
        }
      />
      {isLoading ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : checkOrderDatailsEmpty() ? (
        <EmptySearchList />
      ) : (
        <SectionList
          renderSectionHeader={renderSection}
          keyExtractor={keyExtractor}
          contentContainerStyle={styles.containerStyle}
          sections={sortUpdates()}
          renderItem={renderItem}
          keyboardShouldPersistTaps="handled"
        />
      )}
      {!forNotDelivered && !isSearchEnabled ? (
        <View style={styles.bottomStrip}>
          <Cash
            status={
              ordersData?.usersToBeDelivered?.length > 0 ||
              ordersData?.usersToBeDeliveredFirstTime?.length > 0
                ? ordersData?.amount?.toPay === 0 &&
                  ordersData?.amount?.paid === 0
                  ? dayjs(dayjs().format("YYYY-MM-DD")).isAfter(
                      route?.params?.date,
                    )
                    ? "Delivered"
                    : "Delivery"
                  : ordersData?.amount?.toPay === 0
                    ? "Delivered"
                    : "Delivery"
                : "Delivered"
            }
          />
          <Text style={styles.bottomStripText}>
            {t(
              `${PATH}/${
                ordersData?.usersToBeDelivered?.length ||
                ordersData?.usersToBeDeliveredFirstTime?.length > 0
                  ? ordersData?.amount?.toPay === 0 &&
                    ordersData?.amount?.paid === 0
                    ? dayjs(dayjs().format("YYYY-MM-DD")).isAfter(
                        route?.params?.date,
                      )
                      ? "paid"
                      : "toPay"
                    : ordersData?.amount?.toPay === 0
                      ? "paid"
                      : "toPay"
                  : "paid"
              }`,
            )}
            <Text style={styles.amount}>
              {t(`${PATH}/rs`)}
              {ordersData?.amount?.toPay === 0
                ? ordersData?.amount?.paid.toFixed(2)
                : ordersData?.amount?.toPay.toFixed(2) ?? 0}
            </Text>
          </Text>
        </View>
      ) : null}
      <RTOModal
        rtoFeedbackModal={rtoFeedbackModal}
        closeRTOModal={closeRTOModal}
        actionData={action}
        source="DeliveryOrderList"
        checkFromFPN={checkFromFPN}
      />
    </View>
  );
};

export const PaymentMode = ({ online }) => {
  if (online) {
    return (
      <View style={styles.onlineTag}>
        <Tick visible />
        <Text style={styles.online}>{t(`${PATH}/online`)}</Text>
      </View>
    );
  }
  return (
    <View style={styles.cashTag}>
      <Text style={styles.cash}>{t(`${PATH}/cashPay`)}</Text>
    </View>
  );
};

export const ShipmentTooltip = memo(({ list }) => {
  const [visible, setVisible] = useState(false);
  return (
    <Tooltip
      isVisible={visible}
      disableShadow={true}
      backgroundColor="transparent"
      contentStyle={{
        elevation: 8,
      }}
      arrowStyle={{
        elevation: 8,
      }}
      content={
        <View>
          {list?.map((shipmentId) => (
            <Text>{shipmentId}</Text>
          ))}
        </View>
      }
      placement="bottom"
      onClose={() => setVisible(false)}
    >
      <TouchableOpacity onPress={() => setVisible(true)}>
        <View style={styles.tooltipContainer}>
          <Text style={styles.text3}>{list?.length ?? 0}</Text>
          <Info />
        </View>
      </TouchableOpacity>
    </Tooltip>
  );
});
const styles = StyleSheet.create({
  containerStyle: {
    paddingBottom: 8,
  },
  listView: {
    padding: 16,
    backgroundColor: colors.white,
    borderRadius: 10,
    elevation: 4,
    overflow: "hidden",
  },
  view1: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  view2: {
    flexDirection: "row",
    marginTop: 12,
    alignItems: "center",
  },
  text2: {
    fontSize: scale(14),
    lineHeight: 20,
    color: "rgba(0, 0, 0, 0.56)",
  },
  text3: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "NotoSansDevanagari-Bold",
    marginRight: 6,
  },
  name: {
    fontSize: scale(14),
    lineHeight: 24,
    color: "black",
    fontWeight: "bold",
    maxWidth: Dimensions.get("window").width * 0.6,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  newTagText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontWeight: "700",
    fontSize: 11,
    color: "#FFFFFF",
    textAlign: "center",
  },
  newTag: {
    position: "absolute",
    left: -20,
    top: 3,
    height: 15,
    width: 60,
    alignItems: "center",
    justifyContent: "center",
    transform: [{ rotate: "-45deg" }],
    backgroundColor: "#00BA6E",
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  onlineTag: {
    borderRadius: 20,
    borderWidth: 1,
    paddingHorizontal: 6,
    alignItems: "center",
    marginHorizontal: 8,
    justifyContent: "center",
    borderColor: colors.lighterBlack,
    flexDirection: "row",
  },
  cashTag: {
    backgroundColor: colors.plain_green,
    borderRadius: 40,
    paddingHorizontal: 6,
    alignItems: "center",
    marginHorizontal: 8,
    justifyContent: "center",
  },
  online: {
    color: colors.lightBlack,
    fontSize: 10,
    lineHeight: 16,
    fontFamily: "NotoSansDevanagari-Bold",
    marginLeft: 4,
  },
  cash: {
    color: "white",
    fontSize: 10,
    lineHeight: 16,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  bottomStrip: {
    padding: 16,
    backgroundColor: colors.white,
    flexDirection: "row",
    alignItems: "center",
    elevation: 20,
  },
  bottomStripText: {
    fontSize: 14,
    lineHeight: 18,
    marginHorizontal: 8,
    color: colors.lightBlack,
  },
  amount: {
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.black,
    fontSize: 16,
    lineHeight: 24,
  },
  helpBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.2)",
    borderRadius: 8,
    justifyContent: "center",
  },
  helpBtnText: {
    fontSize: 14,
    fontFamily: "NotoSansDevanagari-Bold",
    marginLeft: 5,
    lineHeight: 20,
  },
  separator: {
    backgroundColor: colors.lighterBlack,
    height: 24,
    width: 1,
    marginHorizontal: 24,
  },
  sectionHeaderText: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "NotoSansDevanagari-Bold",
    marginHorizontal: 6,
  },
  address: {
    maxWidth: "80%",
    color: colors.lightBlack,
    fontSize: 14,
    fontFamily: "NotoSansDevanagari-Regular",
  },
  cardContainer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  tooltipContainer: {
    flexDirection: "row",
    alignItems: "center",
    left: -12,
    paddingHorizontal: 12,
  },
  neededButtonContainer: {
    marginTop: 16,
  },
  orderNeededInstructions: {
    flexDirection: "row",
    marginTop: 16,
  },
  orderNeeded: {
    color: colors.mediumBlack,
    marginStart: 10,
  },
  footerImage: {
    width: 24,
    height: 24,
  },
});

export default DeliveryOrdersList;
